<template>
  <section>
    <!-- Header -->
    <Container>
      <HeadingLarge>
        Plan Information
      </HeadingLarge>
    </Container>
    <div :class="$style.body">
      <div :class="$style.planDescriptionWrapper">
        <!-- Plan -->
        <Container
          :class="$style.planContainer"
        >
          <HeadingSmall
            :class="$style.inline"
            data-test-id="PlanInformation__planName"
          >
            {{ plan.name }}
          </HeadingSmall>
          <HeadingSmall
            v-if="hasSignedContract && !hasLegacyPlan"
            :class="[$style.inline, $style.subheader]"
            data-test-id="PlanInformation__billed"
            :hasMargin="true"
            weight="normal"
          >
            (billed {{ plan.contractType.toLowerCase() }})
          </HeadingSmall>
          <ParagraphBody
            v-if="hasLegacyPlan"
            data-test-id="PlanInformation__hasLegacyPlan"
          >
            Your account is on a legacy plan, please contact your account manager
            to update your plan -
            <Link
              :href="`mailto:${$options.accountEmail}`"
              target="_blank"
              :text="$options.accountEmail"
            />
          </ParagraphBody>
          <div v-else>
            <ParagraphBody
              v-if="isCustomer"
              data-test-id="PlanInformation__planSelected"
            >
              You selected {{ plan.name.trim().concat('.') }}
              <span v-if="isNonProfitOrEducationPlan && hasRecommendations">
                To update your plan, please contact your account manager
                -
                <Link
                  :href="`mailto:${$options.accountEmail}`"
                  target="_blank"
                  :text="$options.accountEmail"
                />.
              </span>
              <span
                v-if="!plan.contractType"
                data-test-id="PlanInformation__startBilling"
              >
                We will only start billing you once you add your 11th user.
              </span>
            </ParagraphBody>
            <ParagraphBody v-else>
              To learn more about other plans see
              <Button
                v-if="hasPlan"
                :class="$style.pricingLink"
                data-test-id="PlanInformation__externalPricingPage"
                hasNoBackground
                isExternal
                :text="$options.pricingPageLinkText"
                textCase="lowercase"
                @click="handleExternalPricingPageLink"
              />
              <ButtonLink
                v-else
                data-test-id="PlanInformation__pricingPageLink"
                :text="$options.pricingPageLinkText"
                textCase="lowercase"
                @click="handlePricingLinkClicked"
              />
            </ParagraphBody>
            <ParagraphBody
              v-if="!isCustomer"
              data-test-id="PlanInformation__tryAllCopy"
            >
              Try all our features for free with up to 10 users.
              When you add your 11th user, pick the package
              that best fits your organizations needs.
            </ParagraphBody>
            <ParagraphBody
              v-else-if="isCustomer && canUpgradePlan && !isNonProfitOrEducationPlan"
              data-test-id="PlanInformation__zeroTrustCopy"
            >
              Zero Trust Access and Premium Support may be added to your
              {{ plan.name }} Plan at an extra cost.
            </ParagraphBody>
          </div>
        </Container>
        <!-- Features -->
        <Container
          v-if="planFeatures.length"
          data-test-id="features"
        >
          <HeadingExtraSmall
            :class="$style.listHeader"
            data-test-id="PlanInformation__featuresIncluded"
            weight="normal"
          >
            Features included in {{ plan.name }}:
          </HeadingExtraSmall>
          <FeaturesGrid
            data-test-id="planFeatures"
            :features="planFeatures"
          />
          <HeadingSmall
            v-if="!isCustomer"
            :class="$style.body"
            data-test-id="PlanInformation__allAdditionalFeatures"
            weight="normal"
          >
            ...and all additional features
          </HeadingSmall>
        </Container>
        <!-- Add Ons -->
        <Container
          v-if="addOns.length"
          data-test-id="addOns"
        >
          <HeadingExtraSmall
            :class="$style.listHeader"
            weight="normal"
          >
            Your add-ons:
          </HeadingExtraSmall>
          <FeaturesGrid
            :data-test-id="$testId('addOnsGrid')"
            :features="addOns"
          />
        </Container>
      </div>
    </div>
  </section>
</template>

<script>
import {
  Button,
  ButtonLink,
  Container,
  HeadingExtraSmall,
  HeadingLarge,
  HeadingSmall,
  Link,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  PricingLink,
  StoreModuleMap,
} from 'client/util/Constants';
import { accountEmail, eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { startCase } from 'lodash';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FeaturesGrid from 'client/Account/FeaturesGrid';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import Util from 'client/util/Util';

const {
  getProductCodeByType,
  FeaturesAddOnsDuplicatedHackList,
  SubscriptionTypes,
} = SubscriptionConstants;

const planModule = 'PlanModule';
const recommendationModule = 'RecommendationModule';
const subscriptionCollection = StoreModuleMap[ItemTypeConsts.subscription];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const {
  description,
  location,
  text,
  toUrl,
  section,
} = eventProperties;

const {
  accountPage,
  upgradePage,
} = eventLocations;

export default {
  name: 'PlanInformation',

  components: {
    Button,
    ButtonLink,
    Container,
    FeaturesGrid,
    HeadingExtraSmall,
    HeadingLarge,
    HeadingSmall,
    Link,
    ParagraphBody,
  },

  accountEmail,
  EventHelper,
  pricingPageLinkText: 'pricing page',
  Util,

  computed: {
    ...mapGetters(recommendationModule, [
      'hasRecommendations',
    ]),

    ...mapState(planModule, [
      'plan',
    ]),

    ...mapGetters(planModule, [
      'canUpgradePlan',
      'getPrimaryEntitlmentFeatures',
      'hasPlan',
    ]),

    ...mapGetters(subscriptionCollection, {
      activeSubscriptions: 'getCollection',
      getSubscription: 'getItem',
    }),

    ...mapState(settingsModule, [
      'isCustomer',
    ]),

    addOns() {
      const addOns = this.plan.addOns.map((addOn) => this.formatCasing(addOn.name));

      const aLaCarteComponents = !this.isPlanALaCarte ? []
        : this.plan.features
          .filter(feature => !this.planFeatures.includes(this.formatCasing(feature.name)))
          .map(feature => this.formatCasing(feature.name));

      return [
        ...aLaCarteComponents,
        ...addOns,
      ];
    },

    hasLegacyPlan() {
      return this.hasPlan
        && !this.isNonProfitOrEducationPlan
        && !this.isPlanALaCarte
        && !this.isPlanIncludedInActiveSubscriptions
        && this.canUpgradePlan;
    },

    hasSignedContract() {
      return this.isCustomer && this.plan.contractType;
    },

    isNonProfitOrEducationPlan() {
      return this.plan.productCode.includes('NP') || this.plan.productCode.includes('EDU');
    },

    isPlanALaCarte() {
      const aLaCarteProductCode = getProductCodeByType(SubscriptionTypes.alaCarte);
      return this.hasPlan && this.plan.productCode === aLaCarteProductCode;
    },

    isPlanIncludedInActiveSubscriptions() {
      return this.activeSubscriptions.some(
        subscription => subscription.productCode === this.plan.productCode,
      );
    },

    planFeatures() {
      if (this.isCustomer && !this.plan) return null;

      const subscription = this.getPrimaryEntitlmentFeatures;
      if (!subscription.length) return [];

      const isFeatureNotPresentInAddOns = (feature) => !this.plan.addOns.some(addOn => {
        const list = FeaturesAddOnsDuplicatedHackList[feature.name];
        return list && list.includes(addOn.name);
      });

      return subscription
        .filter((feature) => isFeatureNotPresentInAddOns(feature))
        .map((feature) => this.formatCasing(feature.name));
    },
  },

  created() {
    // VFA features breakdown should be the same as Platform Plus
    const productCode = this.isCustomer
      ? this.plan.productCode : getProductCodeByType(SubscriptionTypes.platformPlus);
    this.fetchPrimaryEntitlementFeatures(productCode);
  },

  methods: {
    ...mapActions('PlanModule', [
      'fetchPrimaryEntitlementFeatures',
    ]),

    formatCasing(feature) {
      return startCase(feature).replace('Ldap', 'LDAP')
        .replace('Mdm', 'Mobile Device Management (MDM)')
        .replace('Mfa', 'Multi-Factor Authentication (MFA)')
        .replace('Radius', 'RADIUS')
        .replace('Sso', 'Single Sign-On (SSO)')
        .replace('Premium Support', '24x7 support with guaranteed uptime SLA')
        .replace('Jumpcloud Protect', 'JumpCloud Protect');
    },

    handleExternalPricingPageLink() {
      this.$options.EventHelper.triggerLinkClicked({
        [description]: 'opens upgrade tab',
        [location]: upgradePage,
        [text]: this.$options.pricingPageLinkText,
        [toUrl]: PricingLink.pricing,
        [section]: 'Plan Information',
      });

      this.$options.Util.openUrl(PricingLink.pricing);
    },

    handlePricingLinkClicked() {
      this.$options.EventHelper.triggerLinkClicked({
        [description]: 'opens pricing page',
        [location]: accountPage,
        [text]: this.$options.pricingPageLinkText,
        [toUrl]: ItemTypeRoutes[ItemTypeConsts.pricing],
        [section]: 'Plan Information',
      });

      this.$router.push({ name: ItemTypeConsts.pricing });
    },
  },

};
</script>

<style module scoped>
.body {
  padding: var(--jc-spacer-medium) 0;
}

.inline {
  display: inline-block;
}

.listHeader {
  color: var(--jc-text-color-light);
}

.listItem {
  color: var(--jc-color-neutral-text-base);
}

.planContainer {
  background-color: var(--jcInformational100);
}

.planDescriptionWrapper {
  border: var(--jc-border);
  border-radius: var(--jc-border-radius-large);
}

.pricingLink.pricingLink {
  color: var(--jc-color-informational-strong);
}

.subheader {
  padding-left: var(--jc-spacer);
}
</style>
